//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { UnisonColor, UnisonIconType, theme } from '@bvengineering/unison-core';
import React, { useState } from 'react';
import styled from 'styled-components';

import Icon from '@components/atoms/icon/Icon';
import Text from '@components/atoms/text/Text';
import { MediumTooltip } from '@components/molecules/tooltip/Tooltip';
import { Mixins, TextMixin } from '@theme/mixins';
import { ellipsize } from '@utils/stringUtils';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

interface Props
  extends React.HTMLAttributes<HTMLButtonElement | HTMLDivElement>,
    Partial<ITrackableComponent> {
  text: string;
  textMixin?: TextMixin;
  maxLength?: number;
  size: ChipSize;
  decorativeIcon?: UnisonIconType;
  functionalIcon?: UnisonIconType;
  chipType?: ChipType;
  onClick?: () => void;
  isDisabled?: boolean;
  tooltipText?: string;
  tooltipZIndex?: number;
}

export type ChipSize = 'small' | 'large';

export type ChipType = 'default' | 'success' | 'error';

/**
 * The chip atom renders a string of text in a pill shaped container
 * with an optional decorative icon on the left, and an optional
 * functional icon on the right that specifies that the entire chip
 * is clickable. If the given string is longer than the max length,
 * the chip will truncate with an ellipsis in the chip
 * and show the entire string in a tooltip on hover.
 */
export const Chip = ({
  chipType = 'default',
  className = '',
  decorativeIcon,
  functionalIcon,
  isDisabled = false,
  maxLength = 20,
  onClick,
  size,
  text,
  textMixin,
  tooltipText,
  tooltipZIndex,
  trackingId,
  ...buttonProps
}: Props) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const ellipsizedText = ellipsize(text, {
    maxLength,
    noSpaceBeforeEllipsis: true,
  });

  const renderChip = () => (
    <ChipContainer
      chipType={chipType}
      className={className}
      onBlur={() => setIsFocused(false)}
      onClick={onClick}
      onFocus={() => setIsFocused(true)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseMove={() => setIsHovered(true)} // Required when elements transition position but mouse does not "enter" component
      size={size}
      trackingId={trackingId}
      // Render the element as a button when it has an onClick handler. The
      // `disabled` attribute is only valid on a button so apply it here too;
      // otherwise it would forward an invalid prop to the underlying div.
      {...(onClick && {
        as: 'button',
        type: 'button',
        disabled: isDisabled,
        ...buttonProps,
      })}
    >
      {decorativeIcon && <DecorativeIcon name={decorativeIcon} size={size} />}
      {textMixin ? (
        <Text mixin={textMixin}>{ellipsizedText}</Text>
      ) : (
        <>{ellipsizedText}</>
      )}

      {functionalIcon && (
        <FunctionalIcon
          chipType={chipType}
          isFocused={isFocused}
          isHovered={isHovered}
          name={functionalIcon}
          size={size}
        />
      )}
    </ChipContainer>
  );

  return (
    <MediumTooltip
      content={tooltipText ?? text}
      isDisabled={text.length <= maxLength && !tooltipText}
      target={renderChip()}
      zIndex={tooltipZIndex}
    />
  );
};

interface IChipProps {
  chipType: ChipType;
  size: ChipSize;
  isFocused?: boolean;
  isHovered?: boolean;
}

interface IChipColors {
  background: UnisonColor;
  text: UnisonColor;
  functionalIcon: UnisonColor;
  functionalIconHovered: UnisonColor;
}

const chipBackgroundColors: Record<ChipType, IChipColors> = {
  default: {
    background: theme.colors.gray2,
    text: theme.colors.aliases.bodyPrimary,
    functionalIcon: theme.colors.aliases.bodySecondary,
    functionalIconHovered: theme.colors.aliases.bodyPrimary,
  },
  success: {
    background: theme.colors.green0,
    text: theme.colors.aliases.success,
    functionalIcon: theme.colors.aliases.success,
    functionalIconHovered: theme.colors.green9,
  },
  error: {
    background: theme.colors.red0,
    text: theme.colors.aliases.error,
    functionalIcon: theme.colors.aliases.error,
    functionalIconHovered: theme.colors.red9,
  },
};

const ChipContainer = withTracking(
  styled.div<IChipProps>(
    ({ chipType, onClick, size, theme }) => `
    && {
      ${size === 'large' ? Mixins.Text.Body : Mixins.Text.Caption}
      display: inline-flex;
      vertical-align: middle;
      align-items: center;
      background-color: ${chipBackgroundColors[chipType].background};
      color: ${chipBackgroundColors[chipType].text};
      border-radius: ${theme.borderRadii.pill};
      border: none;
      font-family: inherit;
      ${onClick ? 'cursor: pointer;' : ''}
      padding: ${theme.spacings[1]} 12px;
    
      // TODO: add focus style once we get design direction
      &:focus {
        outline: none;
      }
    }
`,
  ),
);

const DecorativeIcon = styled(Icon)<Omit<IChipProps, 'chipType'>>(
  ({ size, theme }) => `
  margin-right: ${theme.spacings[2]};
  width: ${size === 'small' ? '12px' : theme.spacings[3]};
  height: ${size === 'small' ? '12px' : theme.spacings[3]};
`,
);

const FunctionalIcon = styled(Icon)<IChipProps>(
  ({ chipType, isFocused, isHovered, size, theme }) => `
  margin-left: ${theme.spacings[2]};
  width: ${size === 'small' ? '12px' : theme.spacings[3]};
  height: ${size === 'small' ? '12px' : theme.spacings[3]};
  color: ${
    isFocused || isHovered
      ? chipBackgroundColors[chipType].functionalIconHovered
      : chipBackgroundColors[chipType].functionalIcon
  }
`,
);

export default Chip;
